// src/components/FilterBar/index.jsx
import React from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { PublicAPI } from '../../api/public';
import { Link } from 'react-router-dom';
import { 
  REGION_OPTIONS, 
  getRegionByDistrict, 
  getDistrictsByRegion 
} from '../../data/districtToRegionMapping';

const Card = styled.section`
  margin: 12px 0 16px;
  padding: 14px;
  border: 1px solid var(--bdr);
  border-radius: 16px;
  background: var(--card);
  box-shadow: var(--shadow);
`;

const Grid = styled.div`
  display: grid;
  gap: 10px 12px;
  grid-template-columns: repeat(12, 1fr);

  .col-3 { grid-column: span 3; min-width: 180px; }
  .col-2 { grid-column: span 2; min-width: 150px; }
  .col-4 { grid-column: span 4; min-width: 220px; }

  @media (max-width: 1400px) {
    .col-3, .col-2 { grid-column: span 3; }
    .col-4 { grid-column: span 4; }
  }
  @media (max-width: 1200px) {
    .col-3, .col-2 { grid-column: span 4; }
    .col-4 { grid-column: span 6; }
  }
  @media (max-width: 860px) {
    grid-template-columns: repeat(6, 1fr);
    .col-3, .col-2, .col-4 { grid-column: span 6; }
  }
`;

const Field = styled.label`
  display: grid; gap: 6px;
  font-size: 14px;
  span { color: var(--muted); font-size: 12px; }
  select, input {
    width: 100%;
    padding: 10px 12px;
    border-radius: 12px;
    border: 1px solid var(--bdr);
    background: var(--card);
    color: inherit;
    outline: none;
  }
`;

const Range = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
`;

const Actions = styled.div`
  display: flex; gap: 10px; justify-content: flex-end;
  margin-top: 8px;
  button, a {
    padding: 10px 14px;
    border-radius: 12px;
    border: 1px solid var(--bdr);
    background: var(--card);
    font-weight: 700;
  }
  .primary {
    background: var(--primary);
    color: var(--primaryFg);
    border-color: transparent;
  }
`;

/**
 * mode:
 *  - 'newbuild'  → по умолчанию deal_type='new-build'
 *  - 'secondary' → по умолчанию deal_type='secondary'
 *  - 'rent'      → по умолчанию deal_type='rent'
 *  - любое другое/пусто — без предустановки
 */
function FilterBar({ mode = 'newbuild', onSearch, initialParams = {}, isMap = false }) {
  const { t } = useTranslation();

  const [districts, setDistricts] = React.useState([]);
  const [propertyTypes, setPropertyTypes] = React.useState([]);
  const [dealTypes, setDealTypes] = React.useState([]);

  // локальный стейт (значения — это СЛАГИ!)
  const [typeSlug, setTypeSlug] = React.useState(initialParams.property_type || '');
  const [dealTypeSlug, setDealTypeSlug] = React.useState(initialParams.deal_type || '');
  const [districtId, setDistrictId] = React.useState(initialParams.district_id || '');
  const [regionCode, setRegionCode] = React.useState(initialParams.region || '');
  const [bedrooms, setBedrooms] = React.useState(initialParams.bedrooms || '');

  const [priceM2Min, setPriceM2Min] = React.useState(initialParams.price_m2_min || '');
  const [priceM2Max, setPriceM2Max] = React.useState(initialParams.price_m2_max || '');
  const [priceMin, setPriceMin] = React.useState(initialParams.price_min || '');
  const [priceMax, setPriceMax] = React.useState(initialParams.price_max || '');
  const [areaMin, setAreaMin] = React.useState(initialParams.area_min || '');
  const [areaMax, setAreaMax] = React.useState(initialParams.area_max || '');

  // словари
  React.useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const [d, pt, dt] = await Promise.all([
          PublicAPI.districts(),
          PublicAPI.propertyTypes(),
          PublicAPI.dealTypes(),
        ]);
        if (!mounted) return;
        setDistricts(Array.isArray(d?.districts) ? d.districts : Array.isArray(d) ? d : []);
        setPropertyTypes(Array.isArray(pt?.property_types) ? pt.property_types : []);
        setDealTypes(Array.isArray(dt?.deal_types) ? dt.deal_types : []);
      } catch {
        if (!mounted) return;
        setDistricts([]); setPropertyTypes([]); setDealTypes([]);
      }
    })();
    return () => { mounted = false; };
  }, []);

  // предустановка deal_type по mode (если не пришёл извне)
  React.useEffect(() => {
    if (initialParams?.deal_type) {
      setDealTypeSlug(initialParams.deal_type);
      return;
    }
    if (mode === 'newbuild')      setDealTypeSlug('new-build');
    else if (mode === 'secondary') setDealTypeSlug('secondary');
    else if (mode === 'rent')     setDealTypeSlug('rent');
    else setDealTypeSlug('');
  }, [mode, initialParams?.deal_type]);

  // синк при внешних изменениях initialParams
  React.useEffect(() => {
    if (!initialParams) return;
    setTypeSlug(initialParams.property_type || '');
    if (initialParams.deal_type) setDealTypeSlug(initialParams.deal_type);
    setDistrictId(initialParams.district_id || '');
    setRegionCode(initialParams.region || '');
    setBedrooms(initialParams.bedrooms || '');
    setPriceM2Min(initialParams.price_m2_min || '');
    setPriceM2Max(initialParams.price_m2_max || '');
    setPriceMin(initialParams.price_min || '');
    setPriceMax(initialParams.price_max || '');
    setAreaMin(initialParams.area_min || '');
    setAreaMax(initialParams.area_max || '');
  }, [initialParams]);

  // Автоматическое проставление округа при выборе района
  const handleDistrictChange = (e) => {
    const selectedDistrictId = e.target.value;
    setDistrictId(selectedDistrictId);
    
    if (selectedDistrictId) {
      // Находим выбранный район в списке
      const selectedDistrict = districts.find(d => d.id.toString() === selectedDistrictId);
      if (selectedDistrict) {
        const districtName = selectedDistrict.name_ru || selectedDistrict.name || selectedDistrict.name_en;
        const region = getRegionByDistrict(districtName);
        if (region) {
          setRegionCode(region);
        }
      }
    }
  };

  // Обработчик выбора округа - фильтрует районы
  const handleRegionChange = (e) => {
    const selectedRegion = e.target.value;
    setRegionCode(selectedRegion);
    
    // Если выбранный район не принадлежит новому округу, сбрасываем район
    if (selectedRegion && districtId) {
      const selectedDistrict = districts.find(d => d.id.toString() === districtId);
      if (selectedDistrict) {
        const districtName = selectedDistrict.name_ru || selectedDistrict.name || selectedDistrict.name_en;
        const districtRegion = getRegionByDistrict(districtName);
        if (districtRegion !== selectedRegion) {
          setDistrictId('');
        }
      }
    }
  };

  // Фильтрация районов по выбранному округу
  const filteredDistricts = React.useMemo(() => {
    if (!regionCode) return districts;
    
    const regionDistricts = getDistrictsByRegion(regionCode);
    return districts.filter(district => {
      const districtName = district.name_ru || district.name || district.name_en;
      return regionDistricts.includes(districtName);
    });
  }, [districts, regionCode]);

  const submit = (e) => {
    e?.preventDefault?.();
    const params = {};

    // Только то, что бэк реально понимает:
    if (typeSlug) params.property_type = typeSlug;     // slug типа объекта
    if (dealTypeSlug) params.deal_type = dealTypeSlug; // slug типа сделки

    if (districtId) params.district_id = Number(districtId);
    if (regionCode) params.region = regionCode; // добавляем параметр округа
    
    if (bedrooms) {
      const n = Number(bedrooms);
      // Новый бэкенд-фильтр по диапазону комнат
      params.bedrooms_min = n;
      params.bedrooms_max = n;
      // Старый параметр оставим для совместимости (бэк может игнорировать)
      params.bedrooms = n;
    }

    if (priceMin) params.price_min = Number(priceMin);
    if (priceMax) params.price_max = Number(priceMax);

    if (areaMin) params.area_min = Number(areaMin);
    if (areaMax) params.area_max = Number(areaMax);

    // price_m2_* сейчас сервером не поддержаны — их "съест" pickAllowed на клиенте
    if (priceM2Min) params.price_m2_min = Number(priceM2Min);
    if (priceM2Max) params.price_m2_max = Number(priceM2Max);

    onSearch?.(params);
  };

  return (
    <Card as="form" onSubmit={submit}>
      <Grid>
        <Field className="col-2">
          <span>{t('Тип недвижимости') || 'Тип недвижимости'}</span>
          <select value={typeSlug} onChange={(e)=>setTypeSlug(e.target.value)}>
            <option value="">{t('Любой') || 'Любой'}</option>
            {propertyTypes.map(pt => (
              <option key={pt.id} value={pt.slug}>
                {pt.name} {/* name локализован с бэка */}
              </option>
            ))}
          </select>
        </Field>

        <Field className="col-2">
          <span>{t('Вариант') || 'Вариант'}</span>
          <select value={dealTypeSlug} onChange={(e)=>setDealTypeSlug(e.target.value)}>
            <option value="">{t('Любой') || 'Любой'}</option>
            {dealTypes.map(dt => (
              <option key={dt.id} value={dt.slug}>
                {dt.name}
              </option>
            ))}
          </select>
        </Field>

        <Field className="col-3">
          <span>{t('Округ') || 'Округ'}</span>
          <select value={regionCode} onChange={handleRegionChange}>
            <option value="">{t('Любой') || 'Любой'}</option>
            {REGION_OPTIONS.map(region => (
              <option key={region.code} value={region.code}>
                {region.name}
              </option>
            ))}
          </select>
        </Field>

        <Field className="col-3">
          <span>{t('Район') || 'Район'}</span>
          <select value={districtId} onChange={handleDistrictChange}>
            <option value="">{t('Любой') || 'Любой'}</option>
            {filteredDistricts.map(d => (
              <option key={d.id} value={d.id}>
                {d.name_ru || d.name || d.name_en || d.slug}
              </option>
            ))}
          </select>
        </Field>

        <Field className="col-2">
          <span>{t('Комнат') || 'Комнат'}</span>
          <select value={bedrooms} onChange={(e)=>setBedrooms(e.target.value)}>
            <option value="">{t('Любой') || 'Любой'}</option>
            {[1,2,3,4,5].map(n => <option key={n} value={n}>{n}</option>)}
          </select>
        </Field>

        <Field className="col-4">
          <span>{t('Цена за м²') || 'Цена за м²'}</span>
          <Range>
            <input inputMode="numeric" placeholder={t('от') || 'от'} value={priceM2Min} onChange={e=>setPriceM2Min(e.target.value)} />
            <input inputMode="numeric" placeholder={t('до') || 'до'} value={priceM2Max} onChange={e=>setPriceM2Max(e.target.value)} />
          </Range>
        </Field>

        <Field className="col-4">
          <span>{t('Стоимость объекта') || 'Стоимость объекта'}</span>
          <Range>
            <input inputMode="numeric" placeholder={t('от') || 'от'} value={priceMin} onChange={e=>setPriceMin(e.target.value)} />
            <input inputMode="numeric" placeholder={t('до') || 'до'} value={priceMax} onChange={e=>setPriceMax(e.target.value)} />
          </Range>
        </Field>

        <Field className="col-4">
          <span>{t('Площадь, м²') || 'Площадь, м²'}</span>
          <Range>
            <input inputMode="numeric" placeholder={t('от') || 'от'} value={areaMin} onChange={e=>setAreaMin(e.target.value)} />
            <input inputMode="numeric" placeholder={t('до') || 'до'} value={areaMax} onChange={e=>setAreaMax(e.target.value)} />
          </Range>
        </Field>
      </Grid>

      <Actions>
        {!isMap && (
          <Link to="/map">{t('На карте') || 'На карте'}</Link>
        )}
        <button className="primary" type="submit">{t('Найти') || 'Найти'}</button>
      </Actions>
    </Card>
  );
}

export default FilterBar;